import { useState, useEffect, useRef } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { supabase } from '../lib/supabase'

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

function toDateString(date) {
const y = date.getFullYear()
const m = String(date.getMonth() + 1).padStart(2, '0')
const d = String(date.getDate()).padStart(2, '0')
return `${y}-${m}-${d}`
}

export default function AvailabilityCalendar({ listingId, mode = 'view', onDateSelect, selectedDates = [] }) {
const [currentMonth, setCurrentMonth] = useState(new Date())
const [blockedDates, setBlockedDates] = useState(new Set())
const [bookedDates, setBookedDates] = useState(new Set())
const [loading, setLoading] = useState(true)
const [dragSelecting, setDragSelecting] = useState(false)
const [dragStartDate, setDragStartDate] = useState(null)
const [dragOverDate, setDragOverDate] = useState(null)
const [dragAction, setDragAction] = useState(null) // 'block' or 'unblock'
const [saving, setSaving] = useState(false)
const calendarRef = useRef(null)

useEffect(() => {
if (listingId) loadAvailability()
}, [listingId])

const loadAvailability = async () => {
setLoading(true)
const { data: blocked } = await supabase
.from('listing_availability')
.select('blocked_date')
.eq('listing_id', listingId)
setBlockedDates(new Set((blocked || []).map(b => b.blocked_date)))

const { data: bookings } = await supabase
.from('bookings')
.select('start_date, hours')
.eq('listing_id', listingId)
.in('status', ['confirmed', 'pending', 'paid', 'active', 'collected'])

const bookedSet = new Set()
;(bookings || []).forEach(b => {
if (!b.start_date) return
const start = new Date(b.start_date)
const days = Math.max(1, Math.ceil((b.hours || 0) / 24))
for (let i = 0; i < days; i++) {
const d = new Date(start)
d.setDate(d.getDate() + i)
bookedSet.add(toDateString(d))
}
})
setBookedDates(bookedSet)
setLoading(false)
}

// Build the calendar grid
const year = currentMonth.getFullYear()
const month = currentMonth.getMonth()
const firstDay = new Date(year, month, 1)
const lastDay = new Date(year, month + 1, 0)
const startDayOfWeek = firstDay.getDay()
const daysInMonth = lastDay.getDate()

const cells = []
for (let i = 0; i < startDayOfWeek; i++) cells.push(null)
for (let d = 1; d <= daysInMonth; d++) cells.push(d)

const today = new Date()
today.setHours(0, 0, 0, 0)

const navigate = (delta) => {
if (dragSelecting) return
setCurrentMonth(prev => {
const next = new Date(prev)
next.setMonth(next.getMonth() + delta)
return next
})
}

const isCurrentMonth =
currentMonth.getFullYear() === today.getFullYear() &&
currentMonth.getMonth() === today.getMonth()

// Get dates currently being dragged over (the live range preview)
const getDragRangeDates = () => {
if (!dragStartDate || !dragOverDate) return new Set()
const start = new Date(dragStartDate)
const end = new Date(dragOverDate)
const [from, to] = start <= end ? [start, end] : [end, start]
const dates = new Set()
const cur = new Date(from)
while (cur <= to) {
dates.add(toDateString(cur))
cur.setDate(cur.getDate() + 1)
}
return dates
}

const dragRangeDates = getDragRangeDates()

// Start dragging from a day (owner mode)
const handleDayMouseDown = (dateStr, dayDate, isPast, isBooked, isBlocked) => {
if (mode !== 'owner') return
if (isPast || isBooked) return
setDragSelecting(true)
setDragStartDate(dateStr)
setDragOverDate(dateStr)
setDragAction(isBlocked ? 'unblock' : 'block')
}

// While dragging, update the end of the range
const handleDayMouseEnter = (dateStr, dayDate, isPast, isBooked) => {
if (!dragSelecting) return
if (isPast || isBooked) return
setDragOverDate(dateStr)
}

// Touch start (mobile)
const handleDayTouchStart = (dateStr, dayDate, isPast, isBooked, isBlocked) => {
if (mode !== 'owner') return
if (isPast || isBooked) return
setDragSelecting(true)
setDragStartDate(dateStr)
setDragOverDate(dateStr)
setDragAction(isBlocked ? 'unblock' : 'block')
}

// Touch move (mobile) - need to detect which button is under the touch point
const handleTouchMove = (e) => {
if (!dragSelecting) return
e.preventDefault()
const touch = e.touches[0]
const el = document.elementFromPoint(touch.clientX, touch.clientY)
if (el && el.dataset && el.dataset.date) {
const dateStr = el.dataset.date
const isPast = el.dataset.past === 'true'
const isBooked = el.dataset.booked === 'true'
if (!isPast && !isBooked) setDragOverDate(dateStr)
}
}

// Finish dragging — commit changes
const handleEnd = async () => {
if (!dragSelecting) return
setDragSelecting(false)

const rangeDates = Array.from(getDragRangeDates())
if (rangeDates.length === 0) {
setDragStartDate(null)
setDragOverDate(null)
setDragAction(null)
return
}

setSaving(true)
if (dragAction === 'block') {
// Insert dates not already blocked
const toInsert = rangeDates
.filter(d => !blockedDates.has(d) && !bookedDates.has(d))
.map(d => ({ listing_id: listingId, blocked_date: d }))
if (toInsert.length > 0) {
await supabase.from('listing_availability').insert(toInsert)
setBlockedDates(prev => {
const next = new Set(prev)
toInsert.forEach(r => next.add(r.blocked_date))
return next
})
}
} else if (dragAction === 'unblock') {
const toRemove = rangeDates.filter(d => blockedDates.has(d))
if (toRemove.length > 0) {
await supabase
.from('listing_availability')
.delete()
.eq('listing_id', listingId)
.in('blocked_date', toRemove)
setBlockedDates(prev => {
const next = new Set(prev)
toRemove.forEach(d => next.delete(d))
return next
})
}
}
setSaving(false)
setDragStartDate(null)
setDragOverDate(null)
setDragAction(null)
}

// Hirer mode: simple tap to select
const handleHirerClick = (dateStr, dayDate, isPast, isBooked, isBlocked) => {
if (mode !== 'hirer' || !onDateSelect) return
if (isPast || isBlocked || isBooked) return
onDateSelect(dateStr)
}

return (
<div
ref={calendarRef}
onMouseUp={handleEnd}
onMouseLeave={handleEnd}
onTouchEnd={handleEnd}
onTouchMove={handleTouchMove}
style={{ background: 'white', borderRadius: 12, padding: '16px', border: '1px solid var(--border)', userSelect: 'none', touchAction: dragSelecting ? 'none' : 'auto' }}
>
<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
<button
onClick={() => navigate(-1)}
disabled={isCurrentMonth}
style={{
background: 'transparent',
border: '1px solid var(--border-strong)',
borderRadius: 8,
padding: 6,
cursor: isCurrentMonth ? 'not-allowed' : 'pointer',
opacity: isCurrentMonth ? 0.3 : 1,
display: 'flex',
alignItems: 'center',
}}
>
<ChevronLeft size={18} />
</button>
<div style={{ fontWeight: 700, color: 'var(--navy)', fontSize: 15 }}>
{MONTH_NAMES[month]} {year}
</div>
<button
onClick={() => navigate(1)}
style={{
background: 'transparent',
border: '1px solid var(--border-strong)',
borderRadius: 8,
padding: 6,
cursor: 'pointer',
display: 'flex',
alignItems: 'center',
}}
>
<ChevronRight size={18} />
</button>
</div>

<div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, marginBottom: 6 }}>
{DAY_NAMES.map(d => (
<div key={d} style={{ textAlign: 'center', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>
{d}
</div>
))}
</div>

<div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
{cells.map((day, i) => {
if (day === null) return <div key={`blank-${i}`} />
const dayDate = new Date(year, month, day)
const dateStr = toDateString(dayDate)
const isPast = dayDate < today
const isBlocked = blockedDates.has(dateStr)
const isBooked = bookedDates.has(dateStr)
const isSelected = selectedDates.includes(dateStr)
const isInDragRange = dragRangeDates.has(dateStr)
const isToday = toDateString(today) === dateStr

let bg = 'white'
let color = 'var(--navy)'
let border = '1px solid var(--border)'
let cursor = 'default'

if (isPast) {
bg = 'var(--gray-50)'
color = 'var(--gray-400)'
} else if (isBooked) {
bg = 'var(--red-light)'
color = 'var(--red)'
border = '1px solid var(--red-light)'
} else if (isInDragRange && mode === 'owner') {
// Live drag preview
if (dragAction === 'block') {
bg = 'var(--gray-400)'
color = 'white'
border = '1px solid var(--gray-600)'
} else {
bg = 'var(--primary-light)'
color = 'var(--primary)'
border = '1px dashed var(--primary)'
}
cursor = 'pointer'
} else if (isBlocked) {
bg = 'var(--gray-200)'
color = 'var(--gray-600)'
border = '1px solid var(--gray-200)'
cursor = mode === 'owner' ? 'pointer' : 'default'
} else if (isSelected) {
bg = 'var(--gradient-brand)'
color = 'white'
border = '1px solid transparent'
cursor = 'pointer'
} else {
if (mode === 'owner' || mode === 'hirer') cursor = 'pointer'
}

if (isToday && !isPast && !isBooked && !isBlocked && !isInDragRange) {
border = '1px solid var(--primary)'
}

return (
<button
key={dateStr}
data-date={dateStr}
data-past={isPast}
data-booked={isBooked}
data-blocked={isBlocked}
onMouseDown={() => handleDayMouseDown(dateStr, dayDate, isPast, isBooked, isBlocked)}
onMouseEnter={() => handleDayMouseEnter(dateStr, dayDate, isPast, isBooked)}
onTouchStart={() => handleDayTouchStart(dateStr, dayDate, isPast, isBooked, isBlocked)}
onClick={() => handleHirerClick(dateStr, dayDate, isPast, isBooked, isBlocked)}
disabled={isPast || (isBooked && mode === 'owner') || mode === 'view' || saving}
style={{
background: bg,
color,
border,
borderRadius: 8,
aspectRatio: '1',
fontSize: 13,
fontWeight: isToday ? 700 : 500,
cursor,
padding: 0,
position: 'relative',
transition: dragSelecting ? 'none' : 'all 0.12s ease',
opacity: saving ? 0.6 : 1,
}}
>
{day}
</button>
)
})}
</div>

<div style={{ display: 'flex', gap: 12, marginTop: 14, flexWrap: 'wrap', fontSize: 11, color: 'var(--text-muted)' }}>
<div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
<span style={{ width: 14, height: 14, background: 'white', border: '1px solid var(--border-strong)', borderRadius: 4, display: 'inline-block' }} /> Available
</div>
{mode === 'owner' && (
<div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
<span style={{ width: 14, height: 14, background: 'var(--gray-200)', borderRadius: 4, display: 'inline-block' }} /> Blocked (drag to unblock)
</div>
)}
{mode !== 'owner' && (
<div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
<span style={{ width: 14, height: 14, background: 'var(--gray-200)', borderRadius: 4, display: 'inline-block' }} /> Unavailable
</div>
)}
<div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
<span style={{ width: 14, height: 14, background: 'var(--red-light)', borderRadius: 4, display: 'inline-block' }} /> Booked
</div>
</div>

{mode === 'owner' && (
<p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 10, lineHeight: 1.5 }}>
💡 Tap or drag across days to block. Tap or drag across blocked days to unblock.
</p>
)}
</div>
)
}